import { ShowChartOutlined, TrendingUpOutlined } from '@mui/icons-material';
import { CardContainer, Tag } from 'finara-components';
import React from 'react';

import { LoadingState } from '@/components/common/LoadingState';
import { useClientDetail, useClientTransactions } from '@/hooks/queries/useSuperAdminClients';

interface ResumenTabProps {
  clientId: string;
}

export const ResumenTab: React.FC<ResumenTabProps> = ({ clientId }) => {
  // Fetch client detail for products overview
  const { data: clientDetail, isLoading: isLoadingDetail } = useClientDetail(clientId);

  // Fetch recent transactions (limit to 5 most recent)
  const { data: transactionsData, isLoading: isLoadingTransactions } = useClientTransactions({
    organization_id: clientId,
    limit: 5,
    page: 1,
    orderDirection: 'DESC',
  });
  // Format currency
  const formatCurrency = (value: number) => {
    return `$${value.toLocaleString('es-MX', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    })}`;
  };

  // Format date
  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    if (isNaN(date.getTime())) return 'N/A';
    return date.toLocaleDateString('es-MX', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  // Map transaction type to Spanish label
  const getTypeLabel = (type: string) => {
    const typeMap: Record<string, string> = {
      credit: 'Crédito',
      debit: 'Débito',
      transfer: 'Transferencia',
      payroll: 'Nómina',
      stp: 'SPEI',
    };
    return typeMap[type] || type;
  };

  // Map status to Spanish label
  const getStatusLabel = (status: string) => {
    const statusMap: Record<string, string> = {
      pending: 'Pendiente',
      approved: 'Completado',
      rejected: 'Rechazado',
    };
    return statusMap[status] || status;
  };

  // Build products data from client detail
  const productsOverview = clientDetail
    ? [
        {
          name: 'SPEI',
          total: clientDetail.products.spei.total,
          description: `Total: ${formatCurrency(clientDetail.products.spei.total)}`,
        },
        {
          name: 'Nómina',
          total: clientDetail.products.nomina.employeeCount,
          description: `${clientDetail.products.nomina.employeeCount} empleados`,
        },
        {
          name: 'Adelantos',
          total: clientDetail.products.adelantos.total,
          description: `Total activo: ${formatCurrency(clientDetail.products.adelantos.total)}`,
        },
      ]
    : [];

  if (isLoadingDetail || isLoadingTransactions) {
    return <LoadingState message="Cargando resumen..." fullHeight={false} />;
  }

  return (
    <div className="space-y-6">
      <div className="grid grid-cols-1 gap-6">
        {/* Vista Rápida de Productos Activos */}
        <CardContainer>
          <div className="flex items-center gap-2 mb-6">
            <TrendingUpOutlined className="text-muted" />
            <h3 className="text-lg font-semibold text-brand-white">
              Vista Rápida de Productos Activos
            </h3>
          </div>

          <div className="space-y-4">
            {productsOverview.map((product, index) => (
              <div key={index} className="p-4 border border-border rounded-lg">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm font-semibold text-brand-white">{product.name}</p>
                    <p className="text-xs text-muted mt-1">{product.description}</p>
                  </div>
                  <div className="text-right">
                    <TrendingUpOutlined className="text-green-600" fontSize="small" />
                  </div>
                </div>
              </div>
            ))}
          </div>
        </CardContainer>
      </div>

      {/* Movimientos Recientes */}
      <CardContainer>
        <div className="flex items-center gap-2 mb-6">
          <ShowChartOutlined className="text-muted" />
          <h3 className="text-lg font-semibold text-brand-white">Movimientos Recientes</h3>
        </div>

        {transactionsData && transactionsData.data.length > 0 ? (
          <div className="space-y-3">
            {transactionsData.data.map(transaction => (
              <div
                key={transaction.id}
                className="flex flex-col md:!flex-row md:!items-center justify-between p-4 border border-border rounded-lg hover:bg-white/5"
              >
                <div className="flex-1 mb-3 md:!mb-0">
                  <div className="flex items-center gap-3 mb-1">
                    <Tag variant="blue">{getTypeLabel(transaction.type)}</Tag>
                    <Tag
                      variant={
                        transaction.status === 'approved'
                          ? 'green'
                          : transaction.status === 'rejected'
                            ? 'red'
                            : 'yellow'
                      }
                    >
                      {getStatusLabel(transaction.status)}
                    </Tag>
                  </div>
                  <p className="text-sm text-brand-white mt-2">
                    {transaction.description || 'Sin descripción'}
                  </p>
                  <p className="text-xs text-muted mt-1">{formatDate(transaction.createdAt)}</p>
                </div>
                <div className="text-left md:!text-right">
                  <p className="text-lg font-semibold text-brand-white">
                    {formatCurrency(transaction.amount)}
                  </p>
                  <p className="text-xs text-muted">
                    {transaction.side === 'in' ? 'Entrada' : 'Salida'}
                  </p>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <p className="text-center text-muted py-8">No hay movimientos recientes</p>
        )}
      </CardContainer>
    </div>
  );
};
